import React, { useState } from 'react';
import { FaInfoCircle, FaCalendar, FaTag, FaCheckCircle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

interface PricingInfoProps {
  basePrice: number;
}

const PricingInfo: React.FC<PricingInfoProps> = ({ basePrice = 45 }) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);

  const calculationExample = {
    guests: 5,
    nights: 7,
    dates: "June 30 - July 6",
    breakdown: [
      { date: "June 30", season: "Pre-Summer", nights: 1, rate: 40.50, subtotal: 202.50 },
      { date: "July 1-5", season: "Main Holiday", nights: 5, rate: 45.00, subtotal: 1125.00 },
      { date: "July 6", season: "Main Holiday", nights: 1, rate: 45.00, subtotal: 225.00 }
    ],
    cleaning: 80,
    service: 120,
    total: 1327.50 + 80 + 120
  };

  return (
    <div className="bg-white border rounded-lg p-4 mb-4">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setShowDetails(!showDetails)}
      >
          <div className="flex items-center gap-2">
            <FaInfoCircle className="text-primary" />
            <span className="font-semibold">{t('pricing.basePrice')}</span>
          </div>
          <span className="text-sm text-gray-600 font-medium">
            {showDetails ? t('common.showLess') : t('common.showMore')}
          </span>
      </div>

      {showDetails && (
        <div className="mt-4 space-y-4 text-sm">
          {/* Base Price */}
          <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <h4 className="font-semibold text-blue-900 mb-2">{t('pricing.basePrice')}</h4>
            <p className="text-blue-800">
              <strong>€{basePrice} {t('pricing.basePriceText')}</strong>
            </p>
            <p className="text-blue-700 mt-1">
              {t('pricing.minimumRequirements')}
            </p>
          </div>

          {/* Seasonal Pricing */}
          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <FaCalendar className="text-primary" />
              {t('pricing.seasonal')}
            </h4>
            <p className="text-gray-600 mb-2">
              {t('pricing.seasonalText')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="border rounded p-2 bg-gray-50">
                <div className="font-medium">{t('pricing.seasons.low')}</div>
                <div className="text-primary">{t('pricing.seasons.lowDiscount')}</div>
                <div className="text-gray-600">{t('pricing.seasons.lowExample')}</div>
              </div>
              <div className="border rounded p-2 bg-gray-50">
                <div className="font-medium">{t('pricing.seasons.preSummer')}</div>
                <div className="text-primary">{t('pricing.seasons.preSummerDiscount')}</div>
                <div className="text-gray-600">{t('pricing.seasons.preSummerExample')}</div>
              </div>
              <div className="border rounded p-2 bg-gray-50">
                <div className="font-medium">{t('pricing.seasons.mainHoliday')}</div>
                <div className="text-primary">{t('pricing.seasons.mainHolidayDiscount')}</div>
                <div className="text-gray-600">{t('pricing.seasons.mainHolidayExample')}</div>
              </div>
              <div className="border rounded p-2 bg-gray-50">
                <div className="font-medium">{t('pricing.seasons.postSummer')}</div>
                <div className="text-primary">{t('pricing.seasons.postSummerDiscount')}</div>
                <div className="text-gray-600">{t('pricing.seasons.postSummerExample')}</div>
              </div>
              <div className="border rounded p-2 bg-gray-50 md:col-span-2">
                <div className="font-medium">{t('pricing.seasons.christmas')}</div>
                <div className="text-primary">{t('pricing.seasons.christmasDiscount')}</div>
                <div className="text-gray-600">{t('pricing.seasons.christmasExample')}</div>
              </div>
            </div>
          </div>

          {/* Promotional Discounts */}
          <div className="bg-green-50 border border-green-200 rounded p-3">
            <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
              <FaTag className="text-green-700" />
              {t('pricing.promotions')}
            </h4>
            <ul className="space-y-1 text-green-800">
              <li className="flex items-start gap-2">
                <FaCheckCircle className="mt-1 flex-shrink-0" />
                <span><strong>{t('pricing.earlyBird.title')}:</strong> {t('pricing.earlyBird.text')}</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="mt-1 flex-shrink-0" />
                <span><strong>{t('pricing.lastMinute.title')}:</strong> {t('pricing.lastMinute.text')}</span>
              </li>
            </ul>
            <p className="text-green-700 mt-2 text-xs">
              {t('pricing.onlyOne')}
            </p>
          </div>

          {/* Calculation Example */}
          <div>
            <h4 className="font-semibold mb-2">{t('pricing.example')}</h4>
            <div className="border rounded p-3 bg-gray-50">
              <div className="font-medium mb-2">
                {calculationExample.guests} {t('pricing.exampleGuests')} × {calculationExample.nights} {t('pricing.exampleNights')} ({calculationExample.dates})
              </div>
              <div className="space-y-1 text-xs">
                {calculationExample.breakdown.map((item, index) => (
                  <div key={index} className="flex justify-between">
                    <span>{item.date} ({item.season})</span>
                    <span>{item.nights} × {calculationExample.guests} × €{item.rate} = €{item.subtotal.toFixed(2)}</span>
                  </div>
                ))}
                <div className="border-t pt-1 mt-1 font-bold flex justify-between">
                  <span>{t('pricing.exampleSeasonSubtotal')}</span>
                  <span>€{(calculationExample.breakdown.reduce((sum, item) => sum + item.subtotal, 0)).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>{t('pricing.exampleCleaning')}</span>
                  <span>€{calculationExample.cleaning.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>{t('pricing.exampleService')}</span>
                  <span>€{calculationExample.service.toFixed(2)}</span>
                </div>
                <div className="border-t pt-1 mt-1 font-bold text-lg flex justify-between text-primary">
                  <span>{t('pricing.exampleTotal')}</span>
                  <span>€{calculationExample.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default PricingInfo;
